import React, { useCallback, useState } from 'react';
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Switch,
  TableProps,
} from 'antd';
import {
  CheckCircleFilled,
  EditFilled,
  SettingOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import {
  addCutomerReportSetting,
  deleteCutomerReportSetting,
  editCutomerReportSetting,
  searchCutomerReportSettings,
} from '@/adapters/customerReportSetting';
import {
  ICustomerReportSetting,
  ISearchCustomerReportSettingReq,
  ISearchCustomerReportSettingRes,
} from '@/interfaces/customerRerportSetting';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import TableComponent from '@/components/table';
import { debounce } from 'lodash';
import {
  getCustomerReportSettingPeriodTypeName,
  getCustomerReportSettingTypeName,
} from '@/helpers/customerReportSettingHelper';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import {
  ECustomerReportSettingPeriodType,
  ECustomerReportSettingType,
} from '@/enums/ECustomerRerportSetting';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';

const CustomerOrderSetting = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<IDataModal<ICustomerReportSetting>>({
    open: false,
  });
  const [criteria, setCriteria] = useState<ISearchCustomerReportSettingReq>({
    pageSize: 10,
    pageStart: 1,
    search: '',
  });
  const [deleteModal, setDeleteModal] = useState<
    IDataModal<ICustomerReportSetting>
  >({
    open: false,
  });

  const [form] = Form.useForm<ICustomerReportSetting>();
  const { data, isLoading, refetch } = useQuery<
    AxiosResponse<ISearchCustomerReportSettingRes>,
    Error
  >({
    queryKey: ['search-customer-report-setting', criteria],
    queryFn: async () => await searchCutomerReportSettings(criteria),
  });

  const debouncedSearch = useCallback(
    debounce(value => {
      setCriteria({
        ...criteria,
        search: value,
      });
    }, 300),
    [],
  );

  const handleCancel = () => {
    setOpen(false);
  };

  const columns: TableProps<ICustomerReportSetting>['columns'] = [
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-medium',
    },
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      className: 'font-medium',
      align: 'center',
      width: 120,
      render: _ => getCustomerReportSettingTypeName(_),
    },
    {
      title: 'Period',
      dataIndex: 'periodType',
      key: 'periodType',
      className: 'font-medium',
      align: 'center',
      width: 120,
      render: _ => getCustomerReportSettingPeriodTypeName(_),
    },
    {
      title: 'จ',
      dataIndex: 'mon',
      key: 'mon',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'อ',
      dataIndex: 'tues',
      key: 'tues',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'พ',
      dataIndex: 'wed',
      key: 'wed',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'พฤ',
      dataIndex: 'thru',
      key: 'thru',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'ศ',
      dataIndex: 'fri',
      key: 'fri',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'ส',
      dataIndex: 'sat',
      key: 'sat',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'อา',
      dataIndex: 'sun',
      key: 'sun',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'เดือน',
      dataIndex: 'endOfMonth',
      key: 'endOfMonth',
      className: 'font-medium ',
      align: 'center',
      width: 40,
      render: _ =>
        _ && (
          <div className="text-red-700">
            <CheckCircleFilled />
          </div>
        ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      className: 'font-medium ',
      width: 350,
      render: (_, rec) => (
        <>
          <div className="font-bold">{`Email`}</div>
          <div>{`${_}`}</div>
          {rec.cc && (
            <>
              <div className="font-bold">{`CC`}</div>
              {rec.cc.split(',').map(p => {
                return <div key={p}>{p}</div>;
              })}
            </>
          )}
        </>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      className: 'font-medium',
      align: 'center',
      width: 40,
      render: (_, rec) => <EditFilled onClick={() => onEdit(rec)} />,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      className: 'font-medium text-red-700',
      align: 'center',
      width: 40,
      render: (_, rec) => (
        <DeleteFilled
          onClick={() =>
            setDeleteModal({
              open: true,
              item: rec,
            })
          }
        />
      ),
    },
    {
      title: '',
      dataIndex: 'isActive',
      key: 'isActive',
      className: 'font-medium',
      align: 'center',
      width: 40,
      render: (_, rec) => (
        <div className="flex flex-col gap-1">
          <Switch
            checkedChildren="AUTO"
            unCheckedChildren="STOP"
            value={_}
            onChange={async checked =>
              await mutations.mutateAsync({ ...rec, isActive: checked })
            }
          />
        </div>
      ),
    },
  ];

  const mutations = useMutation({
    mutationFn: async (data: Partial<ICustomerReportSetting>) =>
      data?.id
        ? await editCutomerReportSetting(data)
        : await addCutomerReportSetting(data),
    mutationKey: ['actionCustomer'],
    onSuccess: data => {
      if (data?.status !== 200) {
        throw new Error();
      } else {
        const message = 'ตั้งค่าสำเร็จ';
        notification.success({
          message: message,
        });
        setAction({ open: false, item: undefined });
        refetch();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<ICustomerReportSetting>) => {
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
    setAction({ open: false });
  };

  const onCustomerChange = (customerInfo: ICustomer) => {
    form.setFieldsValue({
      customerId: customerInfo.id,
      businessUnitId: customerInfo.businessUnitId,
    });
  };

  const onEdit = (rec: ICustomerReportSetting) => {
    form.setFieldsValue(rec);
    setAction({
      open: true,
      item: rec,
    });
  };

  const onDelete = async () => {
    try {
      await deleteCutomerReportSetting(Number(deleteModal?.item?.id));
      notification.success({
        message: 'ลบสำเร็จ',
      });
      setDeleteModal({ open: false, item: undefined });
      refetch();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <Button type="primary" ghost onClick={() => setOpen(true)}>
        <SettingOutlined /> ตั้งค่า
      </Button>
      <Modal
        title="ตั้งค่า"
        open={open}
        width={'1400px'}
        onCancel={handleCancel}
        cancelText="ยกเลิก"
        footer={false}
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-row gap-2 justify-between">
            <Input.Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={debouncedSearch}
            />
            <Button
              size="large"
              type="primary"
              ghost
              onClick={() =>
                setAction({
                  open: true,
                  item: undefined,
                })
              }
            >
              เพิ่มใหม่
            </Button>
          </div>
          <TableComponent<ICustomerReportSetting>
            columns={columns}
            criteria={{}}
            data={data?.data || { data: [], totalRecords: 0 }}
            loading={isLoading}
            onTableChange={() => console.log()}
          />
        </div>
      </Modal>
      <Modal
        title={'เพิ่มใหม่'}
        open={action.open}
        onOk={() => handleUpdate()}
        onCancel={onReset}
        okText="บันทึก"
        cancelText="ยกเลิก"
        footer={[
          <Button key="back" onClick={onReset}>
            ยกเลิก
          </Button>,
          <Button
            key="submit"
            type="primary"
            form="productGroup"
            onClick={handleUpdate}
          >
            บันทึก
          </Button>,
        ]}
        afterClose={onReset}
      >
        <div className="flex w-full">
          <Form
            form={form}
            name="product"
            onFinish={handleUpdate}
            layout="vertical"
            className="w-full"
            initialValues={{
              mon: false,
              tues: false,
              wed: false,
              thru: false,
              fri: false,
              sat: false,
              sun: false,
              endOfMonth: false,
              isActive: false,
            }}
          >
            <Row gutter={12}>
              <Form.Item<ICustomerReportSetting> name="businessUnitId" hidden>
                <InputNumber type="hidden" />
              </Form.Item>
              <Form.Item<ICustomerReportSetting> name="id" hidden>
                <InputNumber type="hidden" />
              </Form.Item>
              <Col span={24}>
                <Form.Item
                  label="ลูกค้า"
                  name="customerId"
                  rules={[{ required: true, message: 'เลือกลูกค้า' }]}
                >
                  <CustomerSelector
                    handleSelectData={onCustomerChange}
                    allowClear={false}
                    customer={
                      action?.item &&
                      ({
                        id: action.item?.customerId,
                        code: action?.item?.customerCode,
                        name: action?.item?.customerName,
                      } as ICustomer)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item<ICustomerReportSetting>
                  label="ปรเภท"
                  name="type"
                  rules={[{ required: true, message: 'เลือกประเภท' }]}
                >
                  <Select
                    size="large"
                    placeholder="ประเภท"
                    options={[
                      {
                        label: 'EVERY DAY',
                        value: ECustomerReportSettingType.EVERYDAY,
                      },
                      {
                        label: 'EVERY WEEK',
                        value: ECustomerReportSettingType.EVERYWEEK,
                      },
                    ]}
                    onChange={c => {
                      if (c === ECustomerReportSettingType.EVERYDAY) {
                        form.setFieldsValue({
                          mon: true,
                          tues: true,
                          wed: true,
                          thru: true,
                          fri: true,
                          sat: true,
                          sun: true,
                        });
                      } else {
                        form.setFieldsValue({
                          mon: false,
                          tues: false,
                          wed: false,
                          thru: false,
                          fri: false,
                          sat: false,
                          sun: false,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item<ICustomerReportSetting>
                  label="Period"
                  name="periodType"
                  rules={[{ required: true, message: 'เลือกPeriod' }]}
                >
                  <Select
                    size="large"
                    placeholder="Period"
                    options={[
                      {
                        label: 'WEEKLY',
                        value: ECustomerReportSettingPeriodType.WEEKLY,
                      },
                      {
                        label: 'MONTH TO DATE',
                        value: ECustomerReportSettingPeriodType.MONTH_TO_DATE,
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันจันทร์
                    <Form.Item<ICustomerReportSetting>
                      name="mon"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันอังคาร
                    <Form.Item<ICustomerReportSetting>
                      name="tues"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันพุธ
                    <Form.Item<ICustomerReportSetting>
                      name="wed"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันพฤหัสบดี
                    <Form.Item<ICustomerReportSetting>
                      name="thru"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันศุกร์
                    <Form.Item<ICustomerReportSetting>
                      name="fri"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันเสาร์
                    <Form.Item<ICustomerReportSetting>
                      name="sat"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    วันอาทิตย์
                    <Form.Item<ICustomerReportSetting>
                      name="sun"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    <span className="text-blue-700">ทุกสิ้นเดือน</span>
                    <Form.Item<ICustomerReportSetting>
                      name="endOfMonth"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item>
                  <Flex justify="space-between" align="center">
                    <span className="text-red-700">AUTO</span>
                    <Form.Item<ICustomerReportSetting>
                      name="isActive"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="default" />
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item<ICustomerReportSetting>
                  label="Email"
                  name="email"
                  rules={[{ required: true, message: 'ระบุ email' }]}
                >
                  <Input.TextArea rows={2} size="large" placeholder="Email" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item<ICustomerReportSetting> label="CC" name="cc">
                  <Input.TextArea rows={5} size="large" placeholder="CC" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
      <DeleteConfirmModal
        content={`คุณต้องการลบใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ open: false });
        }}
        handleOk={() => {
          onDelete();
        }}
        open={deleteModal?.open}
        title={`ลูกค้า '${deleteModal?.item?.customerName}'`}
      />
    </div>
  );
};

export default CustomerOrderSetting;
